"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { format, addDays, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { vi, enUS } from "date-fns/locale";
import {
   Button,
   Calendar,
   Popover,
   PopoverContent,
   PopoverTrigger
} from "@/app/components/ui";
import { cn } from "@/app/lib/utils";
import { DateRange } from "@/types";

interface DateRangePickerProps {
   dateRange?: DateRange;
   onDateRangeChange: (dateRange: DateRange) => void;
   placeholder?: string;
   className?: string;
   disabled?: boolean;
   showPresets?: boolean;
   minDate?: Date;
   maxDate?: Date;
}

/**
 * Normalizes a date to the start of the day (00:00:00.000) and returns
 * a UTC ISO string — preserves the browser's local timezone during conversion.
 * e.g. GMT+7: Feb 22 00:00:00 → "2026-02-21T17:00:00.000Z"
 */
export const getStartOfDay = (date: Date): Date => startOfDay(date);
export const toUTCISOString = (date: Date): string => date.toISOString();

const PRESET_RANGES = [
   {
      key: "today",
      getRange: (): DateRange => ({
         startDate: toUTCISOString(getStartOfDay(new Date())),
         endDate: toUTCISOString(endOfDay(new Date()))
      })
   },
   {
      key: "yesterday",
      getRange: (): DateRange => ({
         startDate: toUTCISOString(getStartOfDay(addDays(new Date(), -1))),
         endDate: toUTCISOString(endOfDay(addDays(new Date(), -1)))
      })
   },
   {
      key: "last7days",
      getRange: (): DateRange => ({
         startDate: toUTCISOString(getStartOfDay(addDays(new Date(), -6))),
         endDate: toUTCISOString(endOfDay(new Date()))
      })
   },
   {
      key: "thisMonth",
      getRange: (): DateRange => {
         const now = new Date();
         const start = new Date(now.getFullYear(), now.getMonth(), 1);
         const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
         return {
            startDate: toUTCISOString(getStartOfDay(start)),
            endDate: toUTCISOString(endOfDay(end))
         };
      }
   },
   {
      key: "last30days",
      getRange: (): DateRange => ({
         startDate: toUTCISOString(getStartOfDay(addDays(new Date(), -29))),
         endDate: toUTCISOString(endOfDay(new Date()))
      })
   },
   {
      key: "thisYear",
      getRange: (): DateRange => {
         const now = new Date();
         const start = new Date(now.getFullYear(), 0, 1);
         const end = new Date(now.getFullYear(), 11, 31);
         return {
            startDate: toUTCISOString(getStartOfDay(start)),
            endDate: toUTCISOString(endOfDay(end))
         };
      }
   }
];

export function DateRangePicker(props: DateRangePickerProps) {
   const translate = useTranslations("Components.DateRangePicker");
   const locale = useLocale();
   const {
      dateRange,
      onDateRangeChange,
      placeholder = translate("selectDateRange"),
      className,
      disabled = false,
      showPresets = true,
      minDate,
      maxDate
   } = props;
   const [isOpen, setIsOpen] = useState(false);
   const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(dateRange);

   const currentLocale = locale === "vi" ? vi : enUS;

   useEffect(() => {
      if (dateRange) {
         setSelectedRange(dateRange);
      }
   }, [dateRange]);

   const handlePresetSelect = (preset: (typeof PRESET_RANGES)[0]) => {
      const range = preset.getRange();
      setSelectedRange(range);
      onDateRangeChange(range);
      setIsOpen(false);
   };

   const handleClear = () => {
      const emptyRange: DateRange = {
         startDate: "",
         endDate: ""
      };
      setSelectedRange(emptyRange);
      onDateRangeChange(emptyRange);
   };

   const formatDateRange = (range: DateRange | undefined) => {
      if (!range?.startDate) return placeholder;

      if (!range.endDate) {
         return `${format(range.startDate, "yyyy/MM/dd", {
            locale: currentLocale
         })} - ...`;
      }

      if (
         startOfDay(new Date(range.startDate)).getTime() ===
         startOfDay(new Date(range.endDate)).getTime()
      ) {
         return format(range.startDate, "yyyy/MM/dd", {
            locale: currentLocale
         });
      }

      return `${format(range.startDate, "yyyy/MM/dd", {
         locale: currentLocale
      })} - ${format(range.endDate, "yyyy/MM/dd", { locale: currentLocale })}`;
   };

   const isDateInRange = (date: Date) => {
      if (!selectedRange?.startDate || !selectedRange?.endDate) return false;
      return isWithinInterval(date, {
         start: new Date(selectedRange.startDate),
         end: new Date(selectedRange.endDate)
      });
   };

   const isDateSelected = (date: Date) => {
      if (!selectedRange?.startDate) return false;
      const isStart = date.getTime() === new Date(selectedRange.startDate).getTime();
      if (isStart) return true;

      const isEnd =
         selectedRange.endDate &&
         date.getTime() === new Date(selectedRange.endDate).getTime();
      if (isEnd) return true;

      return false;
   };

   const handleCalendarSelect = (date: Date | undefined) => {
      if (!date) {
         if (selectedRange?.startDate && !selectedRange.endDate) {
            const startDateValue = new Date(selectedRange.startDate);
            const newRange: DateRange = {
               startDate: toUTCISOString(getStartOfDay(startDateValue)),
               endDate: toUTCISOString(endOfDay(startDateValue))
            };
            setSelectedRange(newRange);
            onDateRangeChange(newRange);
            setIsOpen(false);
         }
         return;
      }

      if (!selectedRange?.startDate) {
         const newRange: DateRange = {
            startDate: toUTCISOString(getStartOfDay(date)),
            endDate: ""
         };
         setSelectedRange(newRange);
         return;
      }

      if (selectedRange.startDate && !selectedRange.endDate) {
         const startDateValue = new Date(selectedRange.startDate);
         const fromDate = startDateValue < date ? startDateValue : date;
         const toDate = startDateValue < date ? date : startDateValue;

         const newRange: DateRange = {
            startDate: toUTCISOString(getStartOfDay(fromDate)),
            endDate: toUTCISOString(endOfDay(toDate))
         };
         setSelectedRange(newRange);
         onDateRangeChange(newRange);
         setIsOpen(false);
         return;
      }

      const newRange: DateRange = {
         startDate: toUTCISOString(getStartOfDay(date)),
         endDate: ""
      };
      setSelectedRange(newRange);
   };

   return (
      <div className={cn("grid gap-2", className)}>
         <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
               <Button
                  variant="outline"
                  className={cn(
                     "w-full justify-start text-left font-normal",
                     !selectedRange?.startDate && "text-muted-foreground"
                  )}
                  disabled={disabled}
               >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDateRange(selectedRange)}
               </Button>
            </PopoverTrigger>
            <PopoverContent
               className="w-auto p-0 max-h-[450px] overflow-auto"
               align="start"
            >
               <div className="flex flex-col gap-2 p-3">
                  {showPresets && (
                     <div className="grid grid-cols-2 gap-2">
                        {PRESET_RANGES.map((preset) => (
                           <Button
                              key={preset.key}
                              variant="outline"
                              size="sm"
                              onClick={() => handlePresetSelect(preset)}
                              className="text-xs"
                           >
                              {translate(`presets.${preset.key}`)}
                           </Button>
                        ))}
                     </div>
                  )}
                  <div className="border-t pt-3">
                     <Calendar
                        mode="single"
                        defaultMonth={selectedRange?.startDate as Date}
                        selected={selectedRange?.startDate as Date}
                        onSelect={handleCalendarSelect}
                        numberOfMonths={2}
                        locale={currentLocale}
                        disabled={(date) => {
                           if (minDate && date < minDate) return true;
                           if (maxDate && date > maxDate) return true;
                           return false;
                        }}
                        modifiers={{
                           selected: (date) => isDateSelected(date),
                           inRange: (date) => isDateInRange(date)
                        }}
                        modifiersStyles={{
                           selected: {
                              backgroundColor: "hsl(var(--primary))",
                              color: "hsl(var(--primary-foreground))"
                           },
                           inRange: {
                              backgroundColor: "hsl(var(--primary) / 0.1)"
                           }
                        }}
                     />
                  </div>
                  {selectedRange?.startDate && (
                     <div className="flex items-center justify-between border-t pt-3">
                        <Button
                           variant="outline"
                           size="icon"
                           onClick={handleClear}
                           className="p-0 hover:bg-transparent hover:text-destructive"
                           title={translate("clear")}
                           aria-label={translate("clear")}
                        >
                           <X className="h-4 w-4" />
                        </Button>
                        <div className="text-sm text-muted-foreground">
                           {formatDateRange(selectedRange)}
                           {!selectedRange.endDate && (
                              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                                 {translate("selectEndDate")}
                              </span>
                           )}
                        </div>
                     </div>
                  )}
               </div>
            </PopoverContent>
         </Popover>
      </div>
   );
}
